
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOtpTimer } from "./useOtpTimer";
import { useOtpInput } from "./useOtpInput";
const ACCOUNTS_URL = process.env.REACT_APP_ACCOUNTS_URL;
const PROVIDER_APP =  process.env.REACT_APP_PROVIDER_URL
const PATIENT_APP =  process.env.REACT_APP_PATIENT_URL
const PRACTITIONER_APP =  process.env.REACT_APP_PRACTITIONER_URL
const CENTRAL_ADMIN_APP =  process.env.REACT_APP_CENTRAL_ADMIN_URL
const SIGNUP_APP = process.env.REACT_APP_SIGNIN_URL
const OTPVerificationPage = ({ setOpenOTPPage, resend, email, sentTo, isNewSignup, setProfileObject }) => {
 
  const [loading, setLoading] = useState(false);
  const { timer, resendEnabled, resetTimer } = useOtpTimer(30);
  const { otp, inputRefs, handleChange, handleKeyDown, handlePaste } = useOtpInput(6);
   
  const navigate = useNavigate();
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, [ ]);
 

  const handleOtpSubmit = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    const isPhoneNumber = /^\d+$/.test(email);
    try {
      setLoading(true);
      const response = await axios.post(`${ACCOUNTS_URL}/auth/verify-otp`, { otp: code, [isPhoneNumber ? "mobile" : "email"]: email });
      const data = response.data?.data || {};
      toast.success("Verified. Redirecting you now…");
      if (data.access_token) {
        fatchLastLoginApp(data);
      } else {
        // Verified but not registered with a hospital yet – choose one next.
        setProfileObject((prev) => ({ ...prev, userId: data.userId, isLogin: !isNewSignup, setupToken: data.setupToken }));
        setLoading(false);
        navigate("/providerList");
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.response?.data?.error?.message || "We couldn't verify the code. Please try again.");
    }
  };

  async function fatchLastLoginApp(profileResponse) {
    const { access_token, userId } = profileResponse;
    try {
      const response = await axios.get(`${ACCOUNTS_URL}/lastapp/${userId}`);
      const { appName, tenantId } = response.data.data;
      setLoading(false);
      navigateUser(appName, access_token, userId, tenantId);
    } catch (error) {
      if (error.response?.status === 404) {
        createLastAppLogin(profileResponse);
      } else {
        setLoading(false);
        toast.error("Something went wrong. Please try again.");
      }
    }
  }

  async function createLastAppLogin(profileResponse) {
    const { access_token, userId, tenantId, userType } = profileResponse;
    try {
      const response = await axios.post(`${ACCOUNTS_URL}/lastapp`, {
        userId,
        tenantId,
        appName: userType,
      });
      navigateUser(userType, access_token, userId, tenantId);
    } catch (error) {
      toast.error("Error creating last app login. Please try again.");
    } finally {
      setLoading(false);
    }
  }


  const navigateUser = (userType, access_token, userId, tenantId) => {
    const urls = {
      patient:PATIENT_APP ,
      provider: PROVIDER_APP,
      central_admin:CENTRAL_ADMIN_APP,
      practitioner: PRACTITIONER_APP,
    };

    const url = urls[userType]
      ? `${urls[userType]}?vt=${access_token}&ui=${userId}&ti=${tenantId}`
      : SIGNUP_APP;

    window.location.href = url;
  };


  return (
    <div
      className="rounded-3xl border border-slate-200 bg-white/90 p-7 shadow-soft backdrop-blur sm:p-9"
      onKeyDown={(e) => {
        if (e.key === "Enter") handleOtpSubmit();
      }}
    >
      <button
        type="button"
        onClick={() => setOpenOTPPage(false)}
        className="-ml-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-ink-900"
      >
        <ArrowLeft className="h-4 w-4" /> Change email or number
      </button>
      <span className="mt-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
        <ShieldCheck className="h-6 w-6" />
      </span>
      <h2 className="mt-4 text-2xl font-extrabold tracking-tight sm:text-3xl">Enter verification code</h2>
      <p className="mt-2 text-sm text-slate-600">
        We sent a 6-digit code to <span className="break-all font-semibold text-ink-900">{sentTo || email}</span>. It expires in 5 minutes.
      </p>

      <div className="mt-7 grid grid-cols-6 gap-2 sm:gap-3">
        {otp.map((data, index) => (
          <input
            ref={(el) => (inputRefs.current[index] = el)}
            className="h-12 w-full rounded-xl border border-slate-300 bg-white text-center text-xl font-semibold text-ink-900 transition focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 sm:h-14"
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${index + 1}`}
            name="otp"
            key={index}
            value={data}
            onChange={(e) => handleChange(e.target, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>

      <button onClick={handleOtpSubmit} disabled={loading} className="hf-btn mt-6 py-3.5 text-base">
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Verifying…
          </>
        ) : (
          "Continue"
        )}
      </button>

      <p className="mt-6 text-center text-sm text-slate-600">
        Didn't get the code?{" "}
        {resendEnabled ? (
          <button
            type="button"
            className="hf-link"
            onClick={async () => {
              if (await resend()) resetTimer();
            }}
          >
            Resend
          </button>
        ) : (
          <span className="font-medium text-slate-400">Resend in {timer}s</span>
        )}
      </p>
    </div>
  );
};

export default OTPVerificationPage;

